import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as fabric from 'fabric';
import { FiChevronLeft, FiChevronRight, FiPlus, FiTrash2, FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import useEditorStore from '../../stores/useEditorStore';
import { convertSlide } from '../../services/parserConverter';
import { DEFAULT_TEXT_INSET } from '../../constants/editorConstants';
import { toFabricValue, toFabricTextGradient } from '../../utils/gradientUtils';
import { highlightTableCells, clearTableCellHighlight, hitTestTableCell, enterTableEditMode, autoFitRowHeights, recalcTableCells, calcCellPosition } from '../../utils/tableUtils';
import useTableInteraction from '../../hooks/useTableInteraction';
import useSmartGuides from '../../hooks/useSmartGuides';
import '../../utils/fabricTextboxPatch'; // Textbox 하이브리드 줄바꿈 패치
import '../../utils/bulletUtils'; // 글머리 기호 렌더링 패치
import { loadCustomFonts } from '../../services/fontLoader';
import TableContextMenu from './TableContextMenu';
import CanvasContextMenu from './CanvasContextMenu';
import { createLineObject, applyLineControls } from '../../utils/lineUtils';
import usePanning from '../../hooks/usePanning';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';
import { applySelectionStyle } from '../../utils/selectionStyleUtils';
import { findNearestFrame, insertImageIntoFrame, replaceImageInFrame, enterFrameEditMode, exitFrameEditMode, updateFrameEditPosition, createFrameClipPath } from '../../utils/frameUtils';

const EditorCanvas = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const isDrawingRef = useRef(false);
  const startPointRef = useRef(null);
  const tempRectRef = useRef(null);
  const drawBoundsRef = useRef(null); // 드래그 영역 좌표 저장
  const isInitializedRef = useRef(false); // 캔버스 초기화 완료 여부
  const zoomSourceRef = useRef('auto'); // 'auto' | 'wheel'
  const textChangedDuringEditRef = useRef(false); // 텍스트 편집 중 내용 변경 여부
  const frameEditStateRef = useRef(null); // 프레임 편집 모드 상태
  const frameHighlightRef = useRef(null); // 프레임 하이라이트 대상
  const [isFrameEditing, setIsFrameEditing] = useState(false); // 프레임 편집 모드 활성화 여부

  const {
    canvas,
    setCanvas,
    activeTool,
    setActiveTool,
    lineSettings,
    selectedObjects,
    shapeSettings,
    textSettings,
    setSelectedObjects,
    pushHistory,
    syncLayersFromCanvas,
    zoom,
    setZoom,
    canvasSize,
    setContainerSize,
    // 슬라이드 관련
    currentSlideIndex,
    slidesData,
    goToSlide,
    addSlide,
    deleteSlide,
    clearCanvas,
    backgroundColor,
    clipOverflow,
    setClipOverflow,
    overflowMargin,
    centerTrigger,
  } = useEditorStore();

  // 테이블 인터랙션 훅 (클릭/드래그/경계선 리사이즈)
  const { tableBorderDragRef } = useTableInteraction(canvas, pushHistory);
  useSmartGuides(canvas);

  // 커스텀 폰트 로드 (마운트 시 1회)
  useEffect(() => {
    loadCustomFonts().then((fonts) => {
      if (fonts.length > 0) {
        useEditorStore.getState().setCustomFonts(fonts);
      }
    });
  }, []);

  // overflow 모드에서 오브젝트 변경 시 캔버스 크기 재계산 트리거
  const [boundsVersion, setBoundsVersion] = useState(0);

  // 우클릭 컨텍스트 메뉴
  const [contextMenu, setContextMenu] = useState(null); // { x, y, tableGroup, cellRow, cellCol }
  const [canvasContextMenu, setCanvasContextMenu] = useState(null); // { x, y, target, activeObjects }

  // 캔버스 초기화 (기본 1080x1080)
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    // 스토어의 canvasSize 사용 (기본값: 1080x1080)
    const initialSize = useEditorStore.getState().canvasSize;

    const fabricCanvas = new fabric.Canvas(canvasRef.current, {
      width: initialSize.width,
      height: initialSize.height,
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true,
    });

    // 캔버스 커서 설정
    fabricCanvas.hoverCursor = 'move';
    fabricCanvas.moveCursor = 'move';

    setCanvas(fabricCanvas);
    isInitializedRef.current = true;

    // 초기 히스토리 저장
    setTimeout(() => {
      useEditorStore.getState().pushHistory();
    }, 100);

    return () => {
      fabricCanvas.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 캔버스 크기 변경은 스토어의 setCanvasSize에서 처리됨

  // 컨테이너 크기 업데이트
  const updateContainerSize = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    setContainerSize({ width: containerWidth, height: containerHeight });
  }, [setContainerSize]);

  // 컨테이너에 맞는 zoom 계산
  const calculateAutoZoom = useCallback(() => {
    if (!containerRef.current) return 1;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    // 패딩을 위해 85% 영역 사용
    const availableWidth = containerWidth * 0.85;
    const availableHeight = containerHeight * 0.85;

    // 캔버스가 컨테이너에 맞도록 스케일 계산
    const scaleX = availableWidth / canvasSize.width;
    const scaleY = availableHeight / canvasSize.height;

    // 작은 값을 사용하여 비율 유지 (컨테이너에 맞게 확대/축소)
    return Math.min(scaleX, scaleY);
  }, [canvasSize]);

  // 컨테이너 크기 변경 감지
  useEffect(() => {
    updateContainerSize();

    let rafId = null;
    const resizeObserver = new ResizeObserver(() => {
      // requestAnimationFrame으로 래핑하여 ResizeObserver loop 에러 방지
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateContainerSize);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [updateContainerSize]);

  // 캔버스 크기 변경 시에만 zoom 재계산
  useEffect(() => {
    const newZoom = calculateAutoZoom();
    setZoom(newZoom);
  }, [canvasSize, calculateAutoZoom, setZoom]);

  // Fabric.js 줌 적용 (viewportTransform은 scale만, 위치는 스크롤로 처리)
  useEffect(() => {
    if (!canvas || !canvas.lowerCanvasEl) return;
    const clipW = canvasSize.width * zoom;
    const clipH = canvasSize.height * zoom;

    // clip 모드: canvasSize 고정 / overflow 모드: 오브젝트 AABB로 확장
    if (clipOverflow) {
      canvas.viewportTransform = [zoom, 0, 0, zoom, 0, 0];
      canvas.setDimensions({ width: clipW, height: clipH });
    } else {
      let minX = 0, minY = 0;
      let maxX = canvasSize.width, maxY = canvasSize.height;

      canvas.getObjects().forEach(obj => {
        // 프레임 편집 모드 임시 객체는 bounds 계산에서 제외
        if (obj._isFrameEditOverlay || obj._isFrameEditBright || obj._isFrameEditOutline) return;

        const center = obj.getCenterPoint();
        const w = obj.getScaledWidth();
        const h = obj.getScaledHeight();
        const angle = obj.angle || 0;

        let halfW, halfH;
        if (Math.abs(angle % 360) < 0.1) {
          halfW = w / 2;
          halfH = h / 2;
        } else {
          const rad = (angle * Math.PI) / 180;
          const cos = Math.abs(Math.cos(rad));
          const sin = Math.abs(Math.sin(rad));
          halfW = (w * cos + h * sin) / 2;
          halfH = (w * sin + h * cos) / 2;
        }

        minX = Math.min(minX, center.x - halfW);
        minY = Math.min(minY, center.y - halfH);
        maxX = Math.max(maxX, center.x + halfW);
        maxY = Math.max(maxY, center.y + halfH);
      });

      // 오브젝트 바운딩 박스 바깥 여유 마진 (스크롤 시 오브젝트 주변 여백)
      if (minX < 0) minX -= overflowMargin;
      if (minY < 0) minY -= overflowMargin;
      if (maxX > canvasSize.width) maxX += overflowMargin;
      if (maxY > canvasSize.height) maxY += overflowMargin;

      // 하얀 캔버스 중심 기준으로 대칭 확장 (캔버스가 항상 중앙에 위치)
      const cx = canvasSize.width / 2;
      const cy = canvasSize.height / 2;
      const halfExtentX = Math.max(cx, cx - minX, maxX - cx);
      const halfExtentY = Math.max(cy, cy - minY, maxY - cy);

      const contentW = 2 * halfExtentX * zoom;
      const contentH = 2 * halfExtentY * zoom;

      const container = containerRef.current;
      const padding = 80;
      const containerW = container?.clientWidth || contentW + padding;
      const containerH = container?.clientHeight || contentH + padding;

      // 컨테이너 여유 공간 (줌아웃 시 중앙 정렬)
      const availW = Math.max(0, (containerW - padding - contentW) / 2);
      const availH = Math.max(0, (containerH - padding - contentH) / 2);

      const offsetX = (halfExtentX - cx) * zoom + availW;
      const offsetY = (halfExtentY - cy) * zoom + availH;

      canvas.viewportTransform = [zoom, 0, 0, zoom, offsetX, offsetY];
      canvas.setDimensions({ width: contentW + availW * 2, height: contentH + availH * 2 });
    }

    canvas.getObjects().forEach(obj => {
      if (obj.setCoords) obj.setCoords();
    });
    canvas.renderAll();

    // auto/slideChange: 하얀 캔버스 중심으로 스크롤 / wheel/bounds: 스크롤 유지
    if (zoomSourceRef.current === 'auto' || zoomSourceRef.current === 'slideChange') {
      const container = containerRef.current;
      if (container) {
        if (clipOverflow) {
          container.scrollLeft = 0;
          container.scrollTop = 0;
        } else {
          // overflow 모드: 하얀 캔버스 중심이 컨테이너 중심에 오도록 스크롤
          const vpt = canvas.viewportTransform;
          const canvasElW = canvas.lowerCanvasEl?.parentElement?.offsetWidth || 0;
          const canvasElH = canvas.lowerCanvasEl?.parentElement?.offsetHeight || 0;
          const wrapperPad = 40;
          const canvasCenterInEl = vpt[4] + canvasSize.width / 2 * zoom;
          const canvasCenterInElY = vpt[5] + canvasSize.height / 2 * zoom;
          const wrapperOffsetX = Math.max(0, (container.clientWidth - canvasElW - wrapperPad * 2) / 2);
          const wrapperOffsetY = Math.max(0, (container.clientHeight - canvasElH - wrapperPad * 2) / 2);
          container.scrollLeft = wrapperOffsetX + wrapperPad + canvasCenterInEl - container.clientWidth / 2;
          container.scrollTop = wrapperOffsetY + wrapperPad + canvasCenterInElY - container.clientHeight / 2;
        }
      }
    }
    zoomSourceRef.current = 'auto';
  }, [canvas, zoom, canvasSize, clipOverflow, boundsVersion, overflowMargin]);

  // 슬라이드 전환 후 overflow bounds 재계산 (스크롤 리셋 + clip 동작)
  useEffect(() => {
    if (!canvas) return;
    zoomSourceRef.current = 'slideChange';
    setBoundsVersion(v => v + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, currentSlideIndex]);

  // 외부에서 센터링 강제 트리거 (템플릿 로드 등)
  useEffect(() => {
    if (!canvas || centerTrigger === 0) return;
    zoomSourceRef.current = 'slideChange';
    setBoundsVersion(v => v + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, centerTrigger]);

  // overflow 모드에서 오브젝트 이동/추가/삭제 시 캔버스 크기 재계산
  useEffect(() => {
    if (!canvas || clipOverflow) return;

    const updateBounds = () => {
      if (useEditorStore.getState().isCanvasLoading) return;
      if (frameEditStateRef.current) return; // 프레임 편집 중 bounds 재계산 방지
      zoomSourceRef.current = 'bounds';
      setBoundsVersion(v => v + 1);
    };
    canvas.on('object:modified', updateBounds);
    canvas.on('object:added', updateBounds);
    canvas.on('object:removed', updateBounds);

    return () => {
      canvas.off('object:modified', updateBounds);
      canvas.off('object:added', updateBounds);
      canvas.off('object:removed', updateBounds);
    };
  }, [canvas, clipOverflow]);

  // overflow 모드에서 캔버스 영역 배경색 직접 렌더링
  useEffect(() => {
    if (!canvas) return;

    // overflow 모드: Fabric.js 기본 배경을 투명으로 (before:render에서 직접 그림)
    // clip 모드: Fabric.js 기본 배경 사용
    canvas.backgroundColor = clipOverflow ? (backgroundColor || '#ffffff') : 'transparent';

    const drawCanvasBounds = ({ ctx }) => {
      if (clipOverflow) return;
      const vpt = canvas.viewportTransform;
      const offsetX = vpt[4];
      const offsetY = vpt[5];
      const w = canvasSize.width * zoom;
      const h = canvasSize.height * zoom;

      // 프레임 편집 모드 중에는 캔버스 배경도 어둡게 (overlay와 동일 톤)
      if (frameEditStateRef.current) {
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(offsetX, offsetY, w, h);
        ctx.restore();
        return;
      }

      // 배경 + 그림자 (CSS boxShadow '0 25px 50px -12px rgba(0,0,0,0.4)' 와 동일하게)
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
      ctx.shadowBlur = 50;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 25;
      ctx.fillStyle = backgroundColor || '#ffffff';
      ctx.fillRect(offsetX, offsetY, w, h);
      ctx.restore();
    };

    canvas.on('before:render', drawCanvasBounds);
    canvas.renderAll();

    return () => {
      canvas.off('before:render', drawCanvasBounds);
    };
  }, [canvas, clipOverflow, canvasSize, zoom, backgroundColor]);

  // 마우스 휠 줌 핸들러 (커서 위치 기준, 스크롤로 위치 보정)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !canvas) return;

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const oldZoom = canvas.getZoom();
        const delta = e.deltaY > 0 ? -0.05 : 0.05;
        const newZoom = Math.max(0.1, Math.min(2, oldZoom + delta));
        if (newZoom === oldZoom) return;

        // 마우스가 가리키는 캔버스 좌표 (줌 적용 전)
        // overflow 모드에서는 음수 마진 때문에 Fabric.js 캔버스 요소 기준으로 계산
        const fabricWrapperEl = canvas.wrapperEl;
        if (!fabricWrapperEl) return;
        const fabricRect = fabricWrapperEl.getBoundingClientRect();
        const canvasX = (e.clientX - fabricRect.left) / oldZoom;
        const canvasY = (e.clientY - fabricRect.top) / oldZoom;

        // 마우스의 컨테이너 뷰포트 내 위치 (스크롤 보정용)
        const containerRect = container.getBoundingClientRect();
        const mouseInViewX = e.clientX - containerRect.left;
        const mouseInViewY = e.clientY - containerRect.top;

        zoomSourceRef.current = 'wheel';
        setZoom(newZoom);

        // 렌더 후 같은 캔버스 좌표가 마우스 위치에 오도록 스크롤 조정
        requestAnimationFrame(() => {
          const newFabricRect = fabricWrapperEl.getBoundingClientRect();
          const newContainerRect = container.getBoundingClientRect();
          const pointInViewX = (newFabricRect.left - newContainerRect.left) + canvasX * newZoom;
          const pointInViewY = (newFabricRect.top - newContainerRect.top) + canvasY * newZoom;
          container.scrollLeft += (pointInViewX - mouseInViewX);
          container.scrollTop += (pointInViewY - mouseInViewY);
        });
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [canvas, canvasSize, setZoom]);

  // 선택 이벤트 핸들러 - 선택된 객체 타입에 따라 도구 변경
  useEffect(() => {
    if (!canvas) return;

    const handleSelection = () => {
      const activeObjects = canvas.getActiveObjects();
      setSelectedObjects(activeObjects);

      // 테이블 셀 텍스트가 선택된 경우 (편집 모드일 때만 하이라이트/편집 진입)
      if (activeObjects.length === 1 && activeObjects[0]._isTableCellText && activeObjects[0].group?._isTableGroup) {
        const cellText = activeObjects[0];
        const store = useEditorStore.getState();
        if (store.editingGroupId) {
          const cellPos = { row: cellText._cellRow, col: cellText._cellCol };
          highlightTableCells(cellText.group, [cellPos]);
          store.setSelectedTableCells([cellPos]);
          store.setSelectedTableGroup(cellText.group);
          canvas.requestRenderAll();
          // 새 셀 진입 시 텍스트 전체 선택
          if (!cellText.isEditing) {
            cellText.enterEditing();
            cellText.selectAll();
          }
        }
      }

      // 선택된 객체가 1개일 때 해당 타입으로 도구 변경
      if (activeObjects.length === 1) {
        const obj = activeObjects[0];
        const type = obj.type;

        if (type === 'group' && obj._isTextBoxGroup) {
          setActiveTool('text');
        } else if (type === 'group' && obj._isTableGroup) {
          setActiveTool('table');
        } else if (type === 'line') {
          setActiveTool('line');
        } else if (type === 'path') {
          setActiveTool('shape');
        } else if (type === 'i-text' || type === 'textbox' || type === 'text') {
          // 테이블 셀 텍스트는 '표' 탭으로
          if (obj._isTableCellText && obj.group?._isTableGroup) {
            setActiveTool('table');
          } else {
            setActiveTool('text');
          }
        } else if (type === 'image' && obj._isChart) {
          setActiveTool('chart');
        } else if (obj._isFrame || (type === 'image' && obj._isFrame)) {
          setActiveTool('image');
        } else if (type === 'image') {
          setActiveTool('image');
        } else if (['rect', 'circle', 'ellipse', 'triangle', 'polygon'].includes(type)) {
          // 프레임(빈 프레임)은 도형이지만 image 탭으로
          if (obj._isFrame) {
            setActiveTool('image');
          } else {
            setActiveTool('shape');
          }
        }
      }
    };

    const handleSelectionCleared = () => {
      setSelectedObjects([]);
      // 경계선 드래그 중이면 하이라이트 유지
      if (tableBorderDragRef.current) return;
      // 테이블 셀 하이라이트 정리
      const store = useEditorStore.getState();
      if (store.selectedTableGroup) {
        clearTableCellHighlight(store.selectedTableGroup);
        canvas.requestRenderAll();
      }
    };

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    canvas.on('selection:cleared', handleSelectionCleared);

    return () => {
      canvas.off('selection:created', handleSelection);
      canvas.off('selection:updated', handleSelection);
      canvas.off('selection:cleared', handleSelectionCleared);
    };
  }, [canvas, setSelectedObjects, setActiveTool, tableBorderDragRef]);

  // 더블클릭 → 그룹 편집 모드 진입
  useEffect(() => {
    if (!canvas) return;

    // 그룹 편집 모드 진입 공통 로직
    const enterGroupEditMode = (group) => {
      group.set({ subTargetCheck: true, interactive: true });
      group.setCoords();
      group.getObjects().forEach(child => {
        if (child.setCoords) child.setCoords();
        applySelectionStyle(child);
        // 중첩 그룹도 subTargetCheck 활성화 (더블클릭으로 진입 가능하게)
        if (child.type === 'group') {
          child.set({ subTargetCheck: true, interactive: true });
        }
      });
      useEditorStore.getState().setEditingGroupId(group.id);
    };

    // subTargets에서 클릭 위치의 자식 객체 찾기
    const findSubTarget = (e) => {
      return e.subTargets?.[0] || null;
    };

    // 싱글클릭: 이미 선택된 그룹을 클릭하면 편집 모드 진입 + 자식 선택
    const handleClick = (e) => {
      const target = e.target;
      if (!target || target.type !== 'group') return;
      if (target._isTextBoxGroup || target._isTableGroup) return; // 별도 처리

      const currentEditingGroupId = useEditorStore.getState().editingGroupId;
      const activeObject = canvas.getActiveObject();

      // 그룹이 이미 선택된 상태에서 클릭 → 편집 모드 진입 + 자식 선택
      if (activeObject === target && !currentEditingGroupId) {
        enterGroupEditMode(target);
        const subTarget = findSubTarget(e);
        if (subTarget) {
          canvas.setActiveObject(subTarget);
        }
        canvas.requestRenderAll();
      }
    };

    const handleDblClick = (e) => {
      const target = e.target;
      const currentEditingGroupId = useEditorStore.getState().editingGroupId;

      // 프레임 더블클릭: 프레임 편집 모드 진입 (이미 편집 중이면 무시)
      if (target && target._isFrame && !target._isEmptyFrame && !frameEditStateRef.current) {
        // Q6: 스크롤 위치 저장 (viewport 이동 방지)
        const container = containerRef.current;
        const savedScrollLeft = container?.scrollLeft;
        const savedScrollTop = container?.scrollTop;

        const editState = enterFrameEditMode(target, canvas);
        frameEditStateRef.current = editState;  // before renderAll so drawCanvasBounds sees it
        setIsFrameEditing(true);
        canvas.renderAll();

        // Q6: 스크롤 위치 복원
        if (container) {
          requestAnimationFrame(() => {
            container.scrollLeft = savedScrollLeft;
            container.scrollTop = savedScrollTop;
          });
        }
        return;
      }

      // TextBoxGroup 더블클릭: 직접 텍스트 편집 진입
      if (target && target.type === 'group' && target._isTextBoxGroup) {
        enterGroupEditMode(target);
        const textChild = target.getObjects().find(c => ['textbox', 'i-text', 'text'].includes(c.type));
        if (textChild) {
          canvas.setActiveObject(textChild);
          canvas.requestRenderAll();
          if (textChild.enterEditing) { textChild.enterEditing(); textChild.selectAll(); }
        }
        return;
      }

      // TableGroup 더블클릭: 셀 텍스트 편집 진입
      if (target && target.type === 'group' && target._isTableGroup) {
        enterTableEditMode(target);
        target.getObjects().forEach(child => {
          applySelectionStyle(child);
        });

        // hitTestTableCell로 클릭된 셀 계산
        const hitCell = hitTestTableCell(target, e, canvas);

        // 해당 셀의 Textbox 찾기
        let textChild = null;
        if (hitCell) {
          textChild = target.getObjects().find(
            ch => ch._isTableCellText && ch._cellRow === hitCell.row && ch._cellCol === hitCell.col
          );
        }
        if (!textChild) {
          // fallback: subTargets 시도
          const subTarget = e.subTargets?.[0];
          if (subTarget && subTarget._isTableCellText) {
            textChild = subTarget;
          } else if (subTarget && subTarget._isTableCell) {
            textChild = target.getObjects().find(
              ch => ch._isTableCellText && ch._cellRow === subTarget._cellRow && ch._cellCol === subTarget._cellCol
            );
          }
        }
        if (!textChild) {
          textChild = target.getObjects().find(ch => ch._isTableCellText);
        }

        if (textChild) {
          const cellPos = { row: textChild._cellRow, col: textChild._cellCol };
          highlightTableCells(target, [cellPos]);
          useEditorStore.getState().setEditingGroupId(target.id);
          useEditorStore.getState().setSelectedTableGroup(target);
          useEditorStore.getState().setSelectedTableCells([cellPos]);
          canvas.setActiveObject(textChild);
          canvas.requestRenderAll();
          if (textChild.enterEditing) { textChild.enterEditing(); textChild.selectAll(); }
        }
        return;
      }

      // 일반 그룹 더블클릭 (미선택 상태에서): 편집 모드 진입 + 자식 선택
      if (target && target.type === 'group' && !currentEditingGroupId) {
        enterGroupEditMode(target);
        const subTarget = findSubTarget(e);
        if (subTarget) {
          canvas.setActiveObject(subTarget);
        }
        canvas.requestRenderAll();
      }
    };

    canvas.on('mouse:up', handleClick);
    canvas.on('mouse:dblclick', handleDblClick);

    return () => {
      canvas.off('mouse:up', handleClick);
      canvas.off('mouse:dblclick', handleDblClick);
    };
  }, [canvas]);

  // 객체 수정 이벤트 핸들러
  useEffect(() => {
    if (!canvas) return;

    // TextBoxGroup: 코너 핸들(tl/tr/bl/br) 스케일링 후 실제 크기로 변환
    const applyTextBoxGroupCornerScale = (obj) => {
      if (!obj || !obj._isTextBoxGroup) return false;
      const sx = obj.scaleX || 1;
      const sy = obj.scaleY || 1;
      if (sx === 1 && sy === 1) return false;

      const children = obj.getObjects();
      const bgRect = children.find(c => c.type === 'rect');
      const textChild = children.find(c => ['textbox', 'i-text', 'text'].includes(c.type));
      if (!bgRect || !textChild) return false;

      const center = obj.getCenterPoint();
      const marL = textChild._marL ?? DEFAULT_TEXT_INSET.L;
      const marR = textChild._marR ?? DEFAULT_TEXT_INSET.R;
      const marT = textChild._marT ?? DEFAULT_TEXT_INSET.T;
      const marB = textChild._marB ?? DEFAULT_TEXT_INSET.B;

      let newBgW = bgRect.width * sx;
      // 최소 너비 제한
      const minTextW = textChild.wordWrap
        ? (textChild.getMinWidth?.() || 2)
        : (textChild.dynamicMinWidth || 2);
      newBgW = Math.max(newBgW, minTextW + marL + marR);

      const newTextW = Math.max(newBgW - marL - marR, 2);
      textChild.set({ width: newTextW, scaleX: 1, scaleY: 1 });
      textChild.initDimensions();

      const textH = textChild.height;
      const adjustedBgH = textH + marT + marB;
      bgRect.set({ width: newBgW, height: adjustedBgH, scaleX: 1, scaleY: 1 });
      textChild.set({ left: (marL - marR) / 2, top: (marT - marB) / 2 });

      obj.set({ scaleX: 1, scaleY: 1, width: newBgW, height: adjustedBgH });
      obj.set({ left: center.x, top: center.y });
      obj.setCoords();
      return true;
    };

    // TextBoxGroup ml/mr 리사이즈 내부 로직 (wrapWithFixedAnchor로 감싸서 사용)
    const textBoxGroupResizeWidth = (eventData, transform, x, y) => {
      const target = transform.target;
      if (!target._isTextBoxGroup) return false;
      const children = target.getObjects();
      const bgRect = children.find(c => c.type === 'rect');
      const textChild = children.find(c => ['textbox', 'i-text', 'text'].includes(c.type));
      if (!bgRect || !textChild) return false;

      const localPoint = fabric.controlsUtils.getLocalPoint(transform, transform.originX, transform.originY, x, y);
      const localX = localPoint.x;

      // 핸들 방향 검증: originX 기준으로 반대편 방향만 허용 (반전 방지)
      const originVal = transform.originX === 'left' ? -0.5 : transform.originX === 'right' ? 0.5 : 0;
      if ((originVal !== 0) && ((originVal > 0 && localX > 0) || (originVal < 0 && localX < 0))) {
        return false;
      }

      const strokePadding = (bgRect.strokeWidth || 0) / (target.strokeUniform ? target.scaleX : 1);
      const isCentered = transform.originX === 'center' && transform.originY === 'center';
      const multiplier = isCentered ? 2 : 1;

      const marL = textChild._marL ?? DEFAULT_TEXT_INSET.L;
      const marR = textChild._marR ?? DEFAULT_TEXT_INSET.R;
      const marT = textChild._marT ?? DEFAULT_TEXT_INSET.T;
      const marB = textChild._marB ?? DEFAULT_TEXT_INSET.B;

      const oldBgW = bgRect.width;
      let newBgW = Math.abs(localX * multiplier / (target.scaleX || 1)) - strokePadding;

      // 최소 너비 제한: 일반 Textbox와 동일하게 동작
      const minTextW = textChild.wordWrap
        ? (textChild.getMinWidth?.() || 2)
        : (textChild.dynamicMinWidth || 2);
      newBgW = Math.max(newBgW, minTextW + marL + marR);

      if (Math.abs(newBgW - oldBgW) < 0.5) return false;

      const newTextW = Math.max(newBgW - marL - marR, 2);
      textChild.set({ width: newTextW });
      textChild.initDimensions();

      const textH = textChild.height;
      const adjustedBgH = textH + marT + marB;
      bgRect.set({ width: newBgW, height: adjustedBgH });
      textChild.set({ left: (marL - marR) / 2, top: (marT - marB) / 2 });
      target.set({ width: newBgW, height: adjustedBgH });

      return true;
    };

    const handleObjectModified = (e) => {
      const obj = e.target;
      if (applyTextBoxGroupCornerScale(obj)) {
        canvas.requestRenderAll();
      }
      // 테이블 셀 텍스트: text:editing:exited에서 이미 pushHistory 호출하므로 중복 방지
      if (obj?._isTableCellText) return;
      // 프레임 드롭 예정: handleMouseUpForFrame에서 pushHistory 호출하므로 중복 방지
      if (frameHighlightRef.current) return;
      pushHistory();
      syncLayersFromCanvas();
    };

    const handleObjectAdded = (e) => {
      const obj = e.target;
      if (obj) {
        applySelectionStyle(obj);

        // 개별 객체의 컨트롤 커서 스타일 설정 (회전 각도에 맞게 동적 변경)
        if (obj.controls) {
          // 회전 각도에 따라 양방향 커서를 동적으로 반환하는 핸들러
          const cursorMap = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];
          const makeCursorHandler = (baseIndex) => (eventData, control, fabricObj) => {
            const angle = fabricObj.getTotalAngle ? fabricObj.getTotalAngle() : (fabricObj.angle || 0);
            const offset = Math.round(((angle % 360) + 360) % 360 / 45) % 4;
            return cursorMap[(baseIndex + offset) % 4];
          };
          // 대각선: nwse(0) / nesw(2), 가로세로: ew(3) / ns(1)
          if (obj.controls.tl) obj.controls.tl.cursorStyleHandler = makeCursorHandler(0);
          if (obj.controls.br) obj.controls.br.cursorStyleHandler = makeCursorHandler(0);
          if (obj.controls.tr) obj.controls.tr.cursorStyleHandler = makeCursorHandler(2);
          if (obj.controls.bl) obj.controls.bl.cursorStyleHandler = makeCursorHandler(2);
          if (obj.controls.ml) obj.controls.ml.cursorStyleHandler = makeCursorHandler(3);
          if (obj.controls.mr) obj.controls.mr.cursorStyleHandler = makeCursorHandler(3);
          if (obj.controls.mt) obj.controls.mt.cursorStyleHandler = makeCursorHandler(1);
          if (obj.controls.mb) obj.controls.mb.cursorStyleHandler = makeCursorHandler(1);
          if (obj.controls.mtr) obj.controls.mtr.cursorStyle = 'crosshair';

          // Textbox / TextBoxGroup: 상하 컨트롤 숨기기 (높이는 텍스트 내용에 따라 자동)
          if (obj.type === 'textbox' || (obj.type === 'group' && obj._isTextBoxGroup)) {
            obj.setControlsVisibility({
              mt: false,
              mb: false,
            });
          }

          // TextBoxGroup: ml/mr 컨트롤을 Textbox처럼 리사이즈 모드로 교체
          if (obj.type === 'group' && obj._isTextBoxGroup) {
            const wrappedResize = fabric.controlsUtils.wrapWithFixedAnchor(textBoxGroupResizeWidth);
            obj.controls.ml = new fabric.Control({
              x: -0.5, y: 0,
              actionHandler: wrappedResize,
              cursorStyleHandler: makeCursorHandler(3),
              actionName: 'resizing',
            });
            obj.controls.mr = new fabric.Control({
              x: 0.5, y: 0,
              actionHandler: wrappedResize,
              cursorStyleHandler: makeCursorHandler(3),
              actionName: 'resizing',
            });
          }

          // Legacy IText (이전 저장 데이터 호환)
          if (obj.type === 'i-text') {
            obj.setControlsVisibility({
              mt: false,
              mb: false,
            });
          }

          // 라인 객체: 커스텀 끝점 컨트롤 적용 (undo/redo 복원 시에도)
          if (obj.type === 'line' && !obj._isTableBorder) {
            applyLineControls(obj);
          }
        }
      }
      syncLayersFromCanvas();
    };

    const handleObjectRemoved = () => {
      syncLayersFromCanvas();
    };

    const handlePathCreated = (e) => {
      if (e.path) {
        e.path.set({
          id: `path-${Date.now()}`,
          name: 'Path',
        });
      }
      pushHistory();
    };

    // 텍스트 내용 변경 시 그라데이션 좌표 실시간 업데이트 + 테이블 행 높이 실시간 조절
    const handleTextChanged = (e) => {
      const textbox = e.target;
      if (!textbox) return;

      // 텍스트 내용이 실제로 변경됨을 기록
      textChangedDuringEditRef.current = true;

      if (textbox._gradientConfig) {
        textbox.set({ fill: toFabricTextGradient(textbox._gradientConfig, textbox) });
      }

      // 테이블 셀 텍스트 처리
      if (textbox._isTableCellText && textbox.group?._isTableGroup) {
        const tableGroup = textbox.group;
        const currentLineCount = textbox._textLines?.length || 1;
        const prevLineCount = textbox._prevLineCount || currentLineCount;
        const scaleY = tableGroup.scaleY || 1;

        // height 동기화: groupH와 sumH가 불일치하면 강제 보정
        const sumH = tableGroup._tableRowHeights.reduce((a, b) => a + b, 0);
        if (Math.abs(tableGroup.height - sumH) > 0.5) {
          const topEdge = tableGroup.top - (tableGroup.height * scaleY) / 2;
          tableGroup.height = sumH;
          tableGroup.top = topEdge + (sumH * scaleY) / 2;
          tableGroup.setCoords();
        }

        // 줄 수 변경 시 행 높이 자동 조절
        if (currentLineCount !== prevLineCount) {
          // autoFit 중 exitEditing 차단: recalcTableCells가 그룹 치수를 변경할 때
          // Fabric.js가 selection 이벤트를 발생시켜 exitEditing → enterEditing + selectAll 순환이 발생
          const origExit = textbox.exitEditing;
          textbox.exitEditing = function() { return this; };

          const topEdgeBefore = tableGroup.top - (tableGroup.height * scaleY) / 2;
          autoFitRowHeights(tableGroup, textbox._cellRow, true);
          tableGroup.top = topEdgeBefore + (tableGroup.height * scaleY) / 2;
          tableGroup.setCoords();
          canvas.contextTopDirty = true;
          textbox._prevLineCount = currentLineCount;

          // exitEditing 복원 + textarea 선택 동기화
          textbox.exitEditing = origExit;
          if (textbox.hiddenTextarea) {
            textbox.hiddenTextarea.selectionStart = textbox.selectionStart;
            textbox.hiddenTextarea.selectionEnd = textbox.selectionEnd;
          }

          // autoFit 후 텍스트 top 위치 보정: recalcTableCells는 편집 중 top을 건드리지 않으므로
          // 행 높이가 변경되면 텍스트가 셀 중심에서 벗어남
          const cellPos = calcCellPosition(
            textbox._cellRow, textbox._cellCol,
            tableGroup._tableColWidths, tableGroup._tableRowHeights,
            textbox._gridSpan || 1, textbox._rowSpan || 1
          );
          textbox.top = cellPos.localCy;
          textbox.setCoords();
          requestAnimationFrame(() => {
            if (textbox.isEditing && textbox.canvas) {
              textbox.cursorOffsetCache = {};
              textbox.renderCursorOrSelection();
            }
          });
        }

        useEditorStore.getState().setActiveTool('text');
      }

      canvas.requestRenderAll();
    };

    // 텍스트 편집 모드 진입 시 스크롤 방지 + 변경 플래그 초기화
    const handleTextEditingEntered = (e) => {
      textChangedDuringEditRef.current = false;
      const textObject = e.target;
      if (textObject?._isTableCellText) {
        textObject._prevLineCount = textObject._textLines?.length || 1;
        // textarea 선택이 textbox 커서와 일치하도록 동기화
        // (autoFit에 의한 exit/re-enter 시 브라우저가 전체선택하는 문제 방지)
        if (textObject.hiddenTextarea) {
          requestAnimationFrame(() => {
            if (textObject.hiddenTextarea && textObject.isEditing) {
              textObject.hiddenTextarea.selectionStart = textObject.selectionStart;
              textObject.hiddenTextarea.selectionEnd = textObject.selectionEnd;
            }
          });
        }
      }
      if (textObject && textObject.hiddenTextarea) {
        textObject.hiddenTextarea.style.position = 'fixed';
        textObject.hiddenTextarea.style.top = '0';
        textObject.hiddenTextarea.style.left = '0';
        textObject.hiddenTextarea.style.opacity = '0';
        textObject.hiddenTextarea.style.pointerEvents = 'none';

        // textarea 전체선택 방지: autoFit에 의한 exit/re-enter 시
        // 브라우저가 textarea를 전체선택하는 것을 감지하여 textbox 커서와 동기화
        const ta = textObject.hiddenTextarea;
        const tb = textObject;
        ta._guardKeydown = (evt) => {
          if (!tb.isEditing) return;
          const isBadSelection = ta.selectionStart === 0
            && ta.selectionEnd > 0
            && ta.selectionEnd === ta.value.length
            && ta.value.length > 1;
          if (isBadSelection && !(evt.key === 'a' && (evt.ctrlKey || evt.metaKey))) {
            ta.value = tb.text;
            ta.selectionStart = tb.selectionStart;
            ta.selectionEnd = tb.selectionEnd;
          }
        };
        ta.addEventListener('keydown', ta._guardKeydown);
      }
    };

    // 텍스트 편집 종료 시 셀 위치 보정 + 히스토리 저장
    const handleTextEditingExited = (e) => {
      const textbox = e.target;
      if (!textbox) return;

      textChangedDuringEditRef.current = false;

      // 편집 중 top 변경을 건너뛴 테이블 셀 textbox를 올바른 위치로 복원
      if (textbox._isTableCellText && textbox.group?._isTableGroup) {
        const tableGroup = textbox.group;
        const savedLeft = tableGroup.left;
        const savedTop = tableGroup.top;
        recalcTableCells(tableGroup);
        tableGroup.set({ left: savedLeft, top: savedTop });
        tableGroup.setCoords();
        canvas.requestRenderAll();
      }

      // 테이블 셀 텍스트: exitGroupEditing 후에 pushHistory되므로 여기서는 스킵
      if (textbox._isTableCellText) return;
      pushHistory();
      syncLayersFromCanvas();
    };

    // Fabric.js v7 버그 수정: renderAll()이 contextTop을 클리어할 때
    // 편집 중인 텍스트의 선택 영역(파란 하이라이트)이 사라지는 문제.
    // renderAll → contextTopDirty 확인 → clearContext(contextTop) 실행하지만
    // 커서 애니메이션이 selection 모드에서 abort되어 재렌더링이 안 됨.
    const handleAfterRender = () => {
      const active = canvas.getActiveObject();
      if (active?.isEditing && active.selectionStart !== active.selectionEnd) {
        active.renderCursorOrSelection();
      }
    };

    // 오브젝트 드래그 시 viewport 밖으로 이동하면 자동 스크롤
    const autoScrollEdge = 40; // 가장자리 감지 영역 (px)
    const autoScrollSpeed = 12; // 스크롤 속도 (px/frame)
    let autoScrollRAF = null;

    const handleObjectMoving = (e) => {
      // ── 자동 스크롤 ──
      const container = containerRef.current;
      if (container) {
        const wrapperEl = canvas.wrapperEl;
        if (wrapperEl) {
          const containerRect = container.getBoundingClientRect();
          const pointer = canvas.getViewportPoint(canvas._lastPointer || { x: 0, y: 0 });
          const wrapperRect = wrapperEl.getBoundingClientRect();
          const mouseX = wrapperRect.left + pointer.x - containerRect.left;
          const mouseY = wrapperRect.top + pointer.y - containerRect.top;

          let dx = 0, dy = 0;
          if (mouseX < autoScrollEdge) dx = -autoScrollSpeed;
          else if (mouseX > containerRect.width - autoScrollEdge) dx = autoScrollSpeed;
          if (mouseY < autoScrollEdge) dy = -autoScrollSpeed;
          else if (mouseY > containerRect.height - autoScrollEdge) dy = autoScrollSpeed;

          if (dx !== 0 || dy !== 0) {
            if (!autoScrollRAF) {
              const doScroll = () => {
                container.scrollLeft += dx;
                container.scrollTop += dy;
                autoScrollRAF = requestAnimationFrame(doScroll);
              };
              autoScrollRAF = requestAnimationFrame(doScroll);
            }
          } else if (autoScrollRAF) {
            cancelAnimationFrame(autoScrollRAF);
            autoScrollRAF = null;
          }
        }
      }

      // ── 프레임 편집 모드 중 이미지 이동 → 밝은 영역 동기화 + 이동 제한 ──
      if (frameEditStateRef.current && e?.target === frameEditStateRef.current.frameImg) {
        updateFrameEditPosition(frameEditStateRef.current, canvas);
        return;
      }

      // ── 프레임 라이브 프리뷰 (이미지 드래그 시) ──
      const movingObj = e?.target;
      if (movingObj && movingObj.type === 'image' && !movingObj._isFrame && !movingObj._isChart) {
        const nearFrame = findNearestFrame(canvas, movingObj);
        const prev = frameHighlightRef.current;

        // 이전 프레임 떠남 → 프리뷰 제거
        if (prev && prev !== nearFrame) {
          if (prev._framePreview) {
            canvas.remove(prev._framePreview);
            prev._framePreview = null;
          }
          if (prev._isEmptyFrame) {
            prev.set({ visible: true, stroke: '#9ca3af', strokeWidth: 2 });
          } else {
            prev.set({ visible: true });
          }
          movingObj.set({ opacity: 1 });
        }

        // 새 프레임 근처 → 프리뷰 생성
        if (nearFrame && nearFrame !== prev) {
          const frameCenter = nearFrame.getCenterPoint();
          const frameW = nearFrame._isEmptyFrame
            ? (nearFrame.width || 100) * (nearFrame.scaleX || 1)
            : (nearFrame._frameWidth || nearFrame.width * nearFrame.scaleX);
          const frameH = nearFrame._isEmptyFrame
            ? (nearFrame.height || 100) * (nearFrame.scaleY || 1)
            : (nearFrame._frameHeight || nearFrame.height * nearFrame.scaleY);
          const shapeType = nearFrame._frameShapeType || 'rect';
          const frameAngle = nearFrame.angle || 0;

          try {
            const srcEl = movingObj.getElement();
            const imgW = srcEl?.naturalWidth || movingObj.width || 100;
            const imgH = srcEl?.naturalHeight || movingObj.height || 100;
            const coverScale = Math.max(frameW / imgW, frameH / imgH);
            const cropW = Math.max(1, Math.round(frameW / coverScale));
            const cropH = Math.max(1, Math.round(frameH / coverScale));
            const cropX = Math.round((imgW - cropW) / 2);
            const cropY = Math.round((imgH - cropH) / 2);

            const preview = new fabric.FabricImage(srcEl, {
              left: frameCenter.x,
              top: frameCenter.y,
              originX: 'center',
              originY: 'center',
              scaleX: coverScale,
              scaleY: coverScale,
              angle: frameAngle,
              cropX, cropY,
              width: cropW,
              height: cropH,
              opacity: 0.75,
              selectable: false,
              evented: false,
            });
            if (shapeType !== 'rect') {
              preview.clipPath = createFrameClipPath(shapeType, cropW, cropH);
            }
            preview._isFramePreview = true;
            canvas.add(preview);
            nearFrame._framePreview = preview;

            // 빈 프레임 숨기기
            if (nearFrame._isEmptyFrame) {
              nearFrame.set({ visible: false });
            } else {
              nearFrame.set({ visible: false });
            }
            movingObj.set({ opacity: 0.4 });
          } catch (err) {
            console.warn('Frame preview error:', err);
          }
        }

        frameHighlightRef.current = nearFrame;
        canvas.renderAll();
      }
    };

    const handleMouseUpForFrame = (e) => {
      // ── 프레임에 이미지 드롭 ──
      const nearFrame = frameHighlightRef.current;
      if (nearFrame) {
        // 프리뷰 제거
        if (nearFrame._framePreview) {
          canvas.remove(nearFrame._framePreview);
          nearFrame._framePreview = null;
        }

        const activeObj = canvas.getActiveObject();
        if (activeObj && activeObj.type === 'image' && !activeObj._isFrame && !activeObj._isChart) {
          activeObj.set({ opacity: 1 });

          // 프레임 복원 후 삽입
          if (nearFrame._isEmptyFrame) {
            nearFrame.set({ visible: true, stroke: '#9ca3af', strokeWidth: 2 });
            insertImageIntoFrame(nearFrame, activeObj, canvas);
          } else {
            nearFrame.set({ visible: true });
            replaceImageInFrame(nearFrame, activeObj, canvas);
          }
          syncLayersFromCanvas();
          pushHistory();
        } else {
          // 드롭 대상이 아님 → 프레임 복원
          if (nearFrame._isEmptyFrame) {
            nearFrame.set({ visible: true, stroke: '#9ca3af', strokeWidth: 2 });
          } else {
            nearFrame.set({ visible: true });
          }
          canvas.renderAll();
        }
        frameHighlightRef.current = null;
      }

      // ── 프레임 편집 모드 종료 (이미지 밖 클릭 시에만) ──
      if (frameEditStateRef.current) {
        const editState = frameEditStateRef.current;
        // e.target이 프레임 이미지이면 유지 (이미지 외부 영역 포함 드래그 허용)
        if (e?.target === editState.frameImg) return;
        // 이미지가 아닌 곳 클릭 → 편집 종료
        frameEditStateRef.current = null;  // before renderAll so drawCanvasBounds sees it
        exitFrameEditMode(editState, canvas);
        setIsFrameEditing(false);
        pushHistory();
      }
    };

    const stopAutoScroll = () => {
      if (autoScrollRAF) {
        cancelAnimationFrame(autoScrollRAF);
        autoScrollRAF = null;
      }
    };

    canvas.on('object:modified', handleObjectModified);
    canvas.on('object:added', handleObjectAdded);
    canvas.on('object:removed', handleObjectRemoved);
    canvas.on('path:created', handlePathCreated);
    canvas.on('text:changed', handleTextChanged);
    canvas.on('text:editing:entered', handleTextEditingEntered);
    canvas.on('text:editing:exited', handleTextEditingExited);
    canvas.on('after:render', handleAfterRender);
    canvas.on('object:moving', handleObjectMoving);
    canvas.on('object:scaling', handleObjectMoving);
    canvas.on('mouse:up', stopAutoScroll);
    canvas.on('mouse:up', handleMouseUpForFrame);

    // 이미 캔버스에 존재하는 객체에도 선택 스타일 적용 (loadFromJSON 등으로 리스너 등록 전에 추가된 경우)
    canvas.getObjects().forEach(obj => handleObjectAdded({ target: obj }));

    return () => {
      stopAutoScroll();
      canvas.off('object:modified', handleObjectModified);
      canvas.off('object:added', handleObjectAdded);
      canvas.off('object:removed', handleObjectRemoved);
      canvas.off('path:created', handlePathCreated);
      canvas.off('text:changed', handleTextChanged);
      canvas.off('text:editing:entered', handleTextEditingEntered);
      canvas.off('text:editing:exited', handleTextEditingExited);
      canvas.off('after:render', handleAfterRender);
      canvas.off('object:moving', handleObjectMoving);
      canvas.off('object:scaling', handleObjectMoving);
      canvas.off('mouse:up', stopAutoScroll);
      canvas.off('mouse:up', handleMouseUpForFrame);
    };
  }, [canvas, pushHistory, syncLayersFromCanvas]);

  // 라인 설정값을 ref로 저장
  const lineSettingsRef = useRef(lineSettings);
  useEffect(() => {
    lineSettingsRef.current = lineSettings;
  }, [lineSettings]);

  // 라인 그리기 모드
  const lineDrawingRef = useRef(false);
  const lineStartRef = useRef(null);
  const tempLineRef = useRef(null);

  // 라인이 선택되어 있으면 설정 모드 → 그리기 비활성화
  const hasSelectedLine = selectedObjects.some(
    obj => obj.type === 'line' && !obj._isTableBorder
  );

  useEffect(() => {
    if (!canvas) return;
    canvas.isDrawingMode = false;

    if (activeTool !== 'line' || hasSelectedLine) {
      // 라인 모드가 아니거나 설정 모드면 임시 라인 정리
      if (tempLineRef.current) {
        canvas.remove(tempLineRef.current);
        tempLineRef.current = null;
      }
      lineDrawingRef.current = false;
      lineStartRef.current = null;
      return;
    }

    canvas.selection = false;

    const handleLineMouseDown = (opt) => {
      if (activeTool !== 'line') return;
      const pointer = canvas.getScenePoint(opt.e);
      lineDrawingRef.current = true;
      lineStartRef.current = { x: pointer.x, y: pointer.y };

      // 미리보기 라인 생성
      const settings = lineSettingsRef.current;
      const previewLine = new fabric.Line(
        [pointer.x, pointer.y, pointer.x, pointer.y],
        {
          stroke: settings.color,
          strokeWidth: settings.width,
          strokeDashArray: settings.strokeDashArray || null,
          opacity: settings.opacity,
          selectable: false,
          evented: false,
        }
      );
      tempLineRef.current = previewLine;
      canvas.add(previewLine);
    };

    const handleLineMouseMove = (opt) => {
      if (!lineDrawingRef.current || !tempLineRef.current) return;
      const pointer = canvas.getScenePoint(opt.e);
      tempLineRef.current.set({ x2: pointer.x, y2: pointer.y });
      canvas.renderAll();
    };

    const handleLineMouseUp = (opt) => {
      if (!lineDrawingRef.current || !lineStartRef.current) return;
      const pointer = canvas.getScenePoint(opt.e);
      const start = lineStartRef.current;

      // 임시 라인 제거
      if (tempLineRef.current) {
        canvas.remove(tempLineRef.current);
        tempLineRef.current = null;
      }

      lineDrawingRef.current = false;
      lineStartRef.current = null;

      // 최소 길이 체크
      const dist = Math.sqrt((pointer.x - start.x) ** 2 + (pointer.y - start.y) ** 2);
      if (dist < 5) return;

      // 라인 생성
      const settings = lineSettingsRef.current;
      const newLine = createLineObject(canvas, start, pointer, settings);
      if (newLine) canvas.setActiveObject(newLine);

      canvas.renderAll();
      useEditorStore.getState().syncLayersFromCanvas();
      useEditorStore.getState().pushHistory();
    };

    canvas.on('mouse:down', handleLineMouseDown);
    canvas.on('mouse:move', handleLineMouseMove);
    canvas.on('mouse:up', handleLineMouseUp);

    return () => {
      canvas.off('mouse:down', handleLineMouseDown);
      canvas.off('mouse:move', handleLineMouseMove);
      canvas.off('mouse:up', handleLineMouseUp);
      canvas.selection = true;
    };
  }, [canvas, activeTool, hasSelectedLine]);

  // 현재 설정값들을 ref로 저장 (핸들러에서 최신값 참조용)
  const activeToolRef = useRef(activeTool);
  const textSettingsRef = useRef(textSettings);
  const shapeSettingsRef = useRef(shapeSettings);

  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  useEffect(() => {
    textSettingsRef.current = textSettings;
  }, [textSettings]);

  useEffect(() => {
    shapeSettingsRef.current = shapeSettings;
  }, [shapeSettings]);

  // 도형/텍스트는 각 패널에서 직접 생성하므로 드래그 생성 비활성화
  const handleMouseDown = useCallback((_opt) => {
    return;
  }, []);

  const handleMouseMove = useCallback((opt) => {
    if (!canvas || !isDrawingRef.current || !tempRectRef.current) return;

    const pointer = opt.scenePoint || { x: opt.e.offsetX, y: opt.e.offsetY };
    const start = startPointRef.current;

    const left = Math.min(start.x, pointer.x);
    const top = Math.min(start.y, pointer.y);
    const width = Math.abs(pointer.x - start.x);
    const height = Math.abs(pointer.y - start.y);

    // 좌표 저장 (mouseUp에서 사용)
    drawBoundsRef.current = { left, top, width, height };

    tempRectRef.current.set({ left, top, width, height });
    canvas.renderAll();
  }, [canvas]);

  const handleMouseUp = useCallback(() => {
    if (!canvas || !isDrawingRef.current) return;

    const currentTool = activeToolRef.current;
    const rect = tempRectRef.current;
    const bounds = drawBoundsRef.current;

    if (rect) {
      canvas.remove(rect);
    }

    isDrawingRef.current = false;
    tempRectRef.current = null;

    if (!bounds) {
      startPointRef.current = null;
      drawBoundsRef.current = null;
      return;
    }

    const { left, top, width, height } = bounds;
    const minSize = 10; // 최소 드래그 크기

    // 드래그 크기가 너무 작으면 무시 (클릭만 한 경우)
    if (width < minSize || height < minSize) {
      startPointRef.current = null;
      drawBoundsRef.current = null;
      return;
    }

    let newObject = null;
    const currentTextSettings = textSettingsRef.current;
    const currentShapeSettings = shapeSettingsRef.current;

    if (currentTool === 'text') {
      const { fontFamily, fontSize, fontColor, bold, italic, lineHeight, charSpacing, wordWrap = false } = currentTextSettings;
      const isWordWrap = wordWrap === true;

      const insetW = width - DEFAULT_TEXT_INSET.L - DEFAULT_TEXT_INSET.R;

      newObject = new fabric.Textbox('텍스트 입력', {
        left: left,
        top: top,
        originX: 'left',
        originY: 'top',
        fontFamily,
        fontSize,
        fill: toFabricValue(fontColor, insetW, height),
        fontWeight: bold ? 'bold' : 'normal',
        fontStyle: italic ? 'italic' : 'normal',
        lineHeight: lineHeight || 1.3,
        charSpacing: charSpacing || 0,
        width: insetW,
        wordWrap: isWordWrap,
        splitByGrapheme: isWordWrap,
        id: `text-${Date.now()}`,
        name: 'Text',
      });
      newObject._marL = DEFAULT_TEXT_INSET.L;
      newObject._marR = DEFAULT_TEXT_INSET.R;
      newObject._marT = DEFAULT_TEXT_INSET.T;
      newObject._marB = DEFAULT_TEXT_INSET.B;
      if (!isWordWrap) {
        newObject._wordJoiners = new RegExp(String.fromCharCode(0)); // eslint-disable-line no-control-regex
        newObject.initDimensions();
      }
    } else if (currentTool === 'shape') {
      const { type, fill, stroke, strokeWidth, strokeDashArray, opacity, rx, ry } = currentShapeSettings;
      const fabricFill = toFabricValue(fill, width, height);
      const fabricStroke = toFabricValue(stroke, width, height);

      switch (type) {
        case 'rect':
          newObject = new fabric.Rect({
            left: left,
            top: top,
            width,
            height,
            originX: 'left',
            originY: 'top',
            fill: fabricFill,
            stroke: fabricStroke,
            strokeWidth,
            strokeDashArray,
            opacity,
            rx: rx || 0,
            ry: ry || 0,
            id: `rect-${Date.now()}`,
            name: 'Rectangle',
          });
          break;
        case 'circle':
          newObject = new fabric.Ellipse({
            left: left,
            top: top,
            rx: width / 2,
            ry: height / 2,
            originX: 'left',
            originY: 'top',
            fill: fabricFill,
            stroke: fabricStroke,
            strokeWidth,
            strokeDashArray,
            opacity,
            id: `circle-${Date.now()}`,
            name: 'Circle',
          });
          break;
        case 'triangle':
          newObject = new fabric.Triangle({
            left: left,
            top: top,
            width,
            height,
            originX: 'left',
            originY: 'top',
            fill: fabricFill,
            stroke: fabricStroke,
            strokeWidth,
            strokeDashArray,
            opacity,
            id: `triangle-${Date.now()}`,
            name: 'Triangle',
          });
          break;
        case 'line':
          newObject = new fabric.Line(
            [left, top, left + width, top + height],
            {
              originX: 'left',
              originY: 'top',
              stroke: fabricStroke,
              strokeWidth,
              strokeDashArray,
              opacity,
              id: `line-${Date.now()}`,
              name: 'Line',
            }
          );
          break;
        default:
          newObject = new fabric.Rect({
            left: left,
            top: top,
            width,
            height,
            originX: 'left',
            originY: 'top',
            fill: fabricFill,
            stroke: fabricStroke,
            strokeWidth,
            id: `rect-${Date.now()}`,
            name: 'Rectangle',
          });
      }
    }

    if (newObject) {
      canvas.add(newObject);
      canvas.setActiveObject(newObject);
      canvas.renderAll();
      pushHistory();

      // 텍스트인 경우 편집 모드 진입
      if (currentTool === 'text' && newObject.enterEditing) {
        newObject.enterEditing();
        newObject.selectAll();
      }
    }

    startPointRef.current = null;
    drawBoundsRef.current = null;
  }, [canvas, pushHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // 도형/텍스트 도구일 때 이벤트 설정
  useEffect(() => {
    if (!canvas) return;

    // 핸들러는 항상 등록하고, 내부에서 activeTool을 체크
    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [canvas, handleMouseDown, handleMouseMove, handleMouseUp]);

  // 패닝 (Space+드래그 / 휠클릭+드래그)
  usePanning(canvas, containerRef);

  // 키보드 단축키
  useKeyboardShortcuts(canvas);

  // 우클릭 컨텍스트 메뉴 (테이블 셀 + 캔버스 범용)
  useEffect(() => {
    if (!canvas) return;

    const handleRightClick = (opt) => {
      const e = opt.e;
      e.preventDefault();
      e.stopPropagation();

      const store = useEditorStore.getState();
      const containerRect = containerRef.current?.getBoundingClientRect();
      const menuX = e.clientX - (containerRect?.left || 0);
      const menuY = e.clientY - (containerRect?.top || 0);

      // 테이블 편집 모드: 테이블 셀 컨텍스트 메뉴 우선
      if (store.editingGroupId) {
        const editingGroup = canvas.getObjects().find(o => o.id === store.editingGroupId);
        if (editingGroup && editingGroup._isTableGroup) {
          const hitCell = hitTestTableCell(editingGroup, opt, canvas);
          if (hitCell) {
            setCanvasContextMenu(null);
            setContextMenu({ x: menuX, y: menuY, tableGroup: editingGroup, cellRow: hitCell.row, cellCol: hitCell.col });
            return;
          }
        }
      }

      // 캔버스 범용 컨텍스트 메뉴
      setContextMenu(null);
      const activeObjects = canvas.getActiveObjects();
      setCanvasContextMenu({ x: menuX, y: menuY, target: opt.target, activeObjects });
    };

    const handleLeftClick = (opt) => {
      if (opt.e.button !== 2) {
        setContextMenu(null);
        setCanvasContextMenu(null);

        // ActiveSelection 바운딩 박스 내부의 빈 공간 클릭 시 선택 해제
        const active = canvas.getActiveObject();
        if (active && active.type === 'activeselection' && opt.target === active) {
          const pointer = canvas.getScenePoint(opt.e);
          const objects = active.getObjects();
          const hit = objects.some(obj => {
            const bound = obj.getBoundingRect();
            return pointer.x >= bound.left && pointer.x <= bound.left + bound.width &&
                   pointer.y >= bound.top && pointer.y <= bound.top + bound.height;
          });
          if (!hit) {
            canvas.discardActiveObject();
            canvas.requestRenderAll();
          }
        }
      }
    };

    canvas.on('mouse:down', handleLeftClick);
    canvas.wrapperEl.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.on('mouse:up', (opt) => {
      if (opt.e.button === 2) handleRightClick(opt);
    });
    return () => {
      canvas.off('mouse:down', handleLeftClick);
      canvas.off('mouse:up');
    };
  }, [canvas]);

  // 캔버스 영역 이미지 드래그앤드롭
  const handleCanvasDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canvas) return;

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // 이미지 파일만 허용 (SVG 포함, PPT/PSD 등 제외)
    // SVG도 래스터화하여 단일 이미지로 추가 (레이어 분리는 업로드탭에서)
    const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
    if (!imageFile) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const img = await fabric.FabricImage.fromURL(evt.target.result);

        // 드롭 위치를 캔버스 좌표로 변환 (viewportTransform 반영)
        const fabricRect = canvas.wrapperEl?.getBoundingClientRect();
        const vpt = canvas.viewportTransform;
        let dropX = canvasSize.width / 2;
        let dropY = canvasSize.height / 2;
        if (fabricRect) {
          dropX = (e.clientX - fabricRect.left - vpt[4]) / vpt[0];
          dropY = (e.clientY - fabricRect.top - vpt[5]) / vpt[3];
        }

        const maxWidth = canvasSize.width * 0.8;
        const maxHeight = canvasSize.height * 0.8;
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

        img.set({
          left: dropX,
          top: dropY,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
          id: `image-${Date.now()}`,
          name: imageFile.name,
        });

        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        syncLayersFromCanvas();
        pushHistory();
        setActiveTool('image');
      } catch (err) {
        console.error('Image drop error:', err);
      }
    };
    reader.readAsDataURL(imageFile);
  }, [canvas, canvasSize, syncLayersFromCanvas, pushHistory, setActiveTool]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCanvasDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 슬라이드가 없으면 1개로 간주 (처음 에디터 진입 시)
  const totalSlides = Math.max(slidesData.length, 1);
  const isLastSlide = currentSlideIndex >= totalSlides - 1;
  const isFirstSlide = currentSlideIndex === 0;

  const handlePrevSlide = () => {
    if (!isFirstSlide) {
      goToSlide(currentSlideIndex - 1, convertSlide);
    }
  };

  const handleNextSlide = () => {
    if (!isLastSlide) {
      goToSlide(currentSlideIndex + 1, convertSlide);
    }
  };

  const handleAddSlide = () => {
    addSlide();
  };

  const handleClearCanvas = () => {
    if (!window.confirm('현재 슬라이드의 모든 레이어를 삭제하시겠습니까?')) return;
    clearCanvas();
    pushHistory();
  };

  const handleDeleteSlide = () => {
    if (totalSlides < 2) return;
    if (!window.confirm(`슬라이드 ${currentSlideIndex + 1}을(를) 삭제하시겠습니까?`)) return;
    deleteSlide();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* 캔버스 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {/* 액션 버튼 오버레이 (스크롤 영향 없음) */}
        <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10, display: 'flex', gap: '6px', pointerEvents: 'auto' }}>
          <button
            onClick={() => setClipOverflow(!clipOverflow)}
            title={clipOverflow ? '캔버스 밖 오브젝트 보이기' : '캔버스 밖 오브젝트 숨기기'}
            style={{
              width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px',
              backgroundColor: clipOverflow ? 'rgba(255,255,255,0.9)' : 'rgba(219,234,254,0.9)',
              color: clipOverflow ? '#6b7280' : '#3b82f6',
              cursor: 'pointer', transition: 'all 0.15s', backdropFilter: 'blur(4px)',
            }}
          >
            {clipOverflow ? <FiEyeOff size={15} /> : <FiEye size={15} />}
          </button>
          <button
            onClick={handleClearCanvas}
            title="모든 레이어 삭제"
            style={{
              width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.9)', color: '#6b7280',
              cursor: 'pointer', transition: 'all 0.15s', backdropFilter: 'blur(4px)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)'; e.currentTarget.style.color = '#6b7280'; }}
          >
            <FiX size={16} />
          </button>
          {totalSlides >= 2 && (
            <button
              onClick={handleDeleteSlide}
              title="현재 슬라이드 삭제"
              style={{
                width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px',
                backgroundColor: 'rgba(255,255,255,0.9)', color: '#6b7280',
                cursor: 'pointer', transition: 'all 0.15s', backdropFilter: 'blur(4px)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)'; e.currentTarget.style.color = '#6b7280'; }}
            >
              <FiTrash2 size={14} />
            </button>
          )}
        </div>

        {/* 스크롤 가능한 캔버스 컨테이너 */}
        <div
          ref={containerRef}
          onDrop={handleCanvasDrop}
          onDragOver={handleCanvasDragOver}
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'auto',
            backgroundColor: isFrameEditing ? 'rgba(0,0,0,0.7)' : '#9ca3af',
            backgroundImage: isFrameEditing ? 'none' : `
              linear-gradient(45deg, #6b7280 25%, transparent 25%),
              linear-gradient(-45deg, #6b7280 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #6b7280 75%),
              linear-gradient(-45deg, transparent 75%, #6b7280 75%)
            `,
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
            transition: 'background-color 0.2s',
          }}
        >
          {/* 스크롤 콘텐츠 래퍼: 캔버스가 작으면 센터링, 크면 스크롤 */}
          <div
            style={{
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: '100%',
              minHeight: '100%',
              boxSizing: 'border-box',
              padding: '40px',
            }}
          >
            <div
              ref={canvasWrapperRef}
              style={{
                backgroundColor: 'transparent',
                boxShadow: clipOverflow ? '0 25px 50px -12px rgba(0, 0, 0, 0.4)' : 'none',
                borderRadius: '2px',
                position: 'relative',
                display: 'inline-block',
                lineHeight: 0,
                flexShrink: 0,
                overflow: clipOverflow ? 'hidden' : 'visible',
              }}
            >
              <canvas ref={canvasRef} />
            </div>
          </div>
        </div>

        {/* 테이블 셀 컨텍스트 메뉴 */}
        <TableContextMenu
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          canvas={canvas}
          pushHistory={pushHistory}
        />

        {/* 캔버스 범용 컨텍스트 메뉴 */}
        <CanvasContextMenu
          contextMenu={canvasContextMenu}
          setContextMenu={setCanvasContextMenu}
          canvas={canvas}
        />
      </div>

      {/* 슬라이드 네비게이션 (항상 표시) */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          padding: '12px',
          backgroundColor: '#f3f4f6',
          borderTop: '1px solid #e5e7eb',
        }}
      >
          {/* 이전 버튼 */}
          <button
            onClick={handlePrevSlide}
            disabled={isFirstSlide}
            style={{
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              backgroundColor: isFirstSlide ? '#f3f4f6' : '#ffffff',
              color: isFirstSlide ? '#9ca3af' : '#374151',
              cursor: isFirstSlide ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <FiChevronLeft size={20} />
          </button>

          {/* 슬라이드 번호 */}
          <span
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              minWidth: '80px',
              textAlign: 'center',
            }}
          >
            {currentSlideIndex + 1} / {totalSlides}
          </span>

          {/* 다음 버튼 또는 추가 버튼 */}
          {isLastSlide ? (
            <button
              onClick={handleAddSlide}
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #3b82f6',
                borderRadius: '8px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              title="새 슬라이드 추가"
            >
              <FiPlus size={20} />
            </button>
          ) : (
            <button
              onClick={handleNextSlide}
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <FiChevronRight size={20} />
            </button>
          )}
        </div>
    </div>
  );
};

export default EditorCanvas;
